import { CreateExerciseDTO, CreateExerciseFactDTO, ExerciseDTO, ExerciseFactDTO } from '@/generated';
import { useExerciseFactsQuery } from '@/hooks/query-hooks';
import { ExerciseFactAutocomplete } from '@/screens/exercise-facts/ExerciseFactAutocomplete';
import { UpdateExerciseFactModal } from '@/screens/exercise-facts/UpdateExerciseFactModal';
import { objectHasId } from '@/utils/create-mode-helper';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RemoveIcon from '@mui/icons-material/Remove';
import { Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

const createEmptyFact = (): CreateExerciseFactDTO => {
  return {
    name: '',
    description: '',
  };
};

export const ExerciseFactsComponent = (props: {
  exercise: ExerciseDTO | CreateExerciseDTO;
  updateExercise: (exercise: Partial<ExerciseDTO | CreateExerciseDTO>) => void;
  onCopy?: () => void;
}) => {
  const { exercise, updateExercise } = props;
  const { exerciseFacts } = useExerciseFactsQuery();
  const [exerciseFact, setExerciseFact] = useState<ExerciseFactDTO | undefined>();
  const [editExerciseFact, setEditExerciseFact] = useState<CreateExerciseFactDTO | ExerciseFactDTO>();

  useEffect(() => {
    //TODO: Improve this kind of logic everywhere.
    //Created, selected, aborted //Maybe a bit unclear in this func.
    //Updates the exercises fact when exercise fact changes via update
    if (exerciseFact) {
      const gt = exerciseFacts?.find((g) => g.id === exerciseFact.id);
      if (gt) setExerciseFact(gt);
    }
  }, [exerciseFacts]);

  return (
    <>
      <UpdateExerciseFactModal
        selectedExerciseId={objectHasId(exercise) ? exercise.id : undefined}
        setExerciseFact={(f) => {
          setEditExerciseFact(f); //TODO: Improve this
        }}
        exeriseFact={editExerciseFact}
      />
      <Stack sx={{ mb: 2 }}>
        <Stack direction={'row'} alignItems={'center'}>
          <Tooltip title="New fact">
            <IconButton
              aria-label="New fact"
              sx={{ color: 'common.white' }}
              onClick={() => {
                setEditExerciseFact(createEmptyFact());
              }}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
          {props.onCopy && (
            <Tooltip title="Copy exercise facts from a progression or regression">
              <IconButton aria-label="Copy exercise facts" sx={{ color: 'common.white' }} onClick={props.onCopy}>
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          )}
          {exerciseFact && (
            <Button
              sx={{}}
              onClick={() => {
                setEditExerciseFact(exerciseFact);
              }}
            >
              Edit Fact
            </Button>
          )}
        </Stack>
        <Stack direction={'row'} alignItems={'center'}>
          <ExerciseFactAutocomplete
            factExerciseId={objectHasId(exercise) ? exercise.id : undefined}
            selectedExerciseFactId={exerciseFact?.id ?? undefined}
            setSelectedExerciseFact={(fact) => {
              setExerciseFact(fact);
            }}
          />
          <IconButton
            aria-label="Add fact"
            disabled={!exerciseFact}
            sx={{ color: 'common.white' }}
            onClick={() => {
              if (!exerciseFact) return;
              updateExercise({ exerciseFactIds: [...(exercise.exerciseFactIds || []), exerciseFact.id] });

              setExerciseFact(undefined);
            }}
          >
            <AddIcon />
          </IconButton>
        </Stack>
        {exercise.exerciseFactIds?.map((factId, index) => (
          <Stack key={`${factId}-${index}`} sx={{ my: 1, gap: 1, flexDirection: 'row', alignItems: 'center' }}>
            <Typography sx={{ flex: 1 }}>{exerciseFacts?.find((ef) => ef.id === factId)?.description}</Typography>
            {exercise.exerciseFactIds && exercise.exerciseFactIds.length > 1 && (
              <>
                <Tooltip title="Move fact up">
                  <span>
                    <IconButton
                      aria-label="Move fact up"
                      disabled={index === 0}
                      onClick={() => {
                        const exerciseFactIds = [...(exercise.exerciseFactIds ?? [])];
                        [exerciseFactIds[index - 1], exerciseFactIds[index]] = [exerciseFactIds[index], exerciseFactIds[index - 1]];
                        updateExercise({ exerciseFactIds });
                      }}
                    >
                      <ArrowUpwardIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Move fact down">
                  <span>
                    <IconButton
                      aria-label="Move fact down"
                      disabled={index === exercise.exerciseFactIds.length - 1}
                      onClick={() => {
                        const exerciseFactIds = [...(exercise.exerciseFactIds ?? [])];
                        [exerciseFactIds[index], exerciseFactIds[index + 1]] = [exerciseFactIds[index + 1], exerciseFactIds[index]];
                        updateExercise({ exerciseFactIds });
                      }}
                    >
                      <ArrowDownwardIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
            <IconButton
              aria-label="Remove fact"
              sx={{ color: 'common.white' }}
              onClick={() => {
                updateExercise({
                  exerciseFactIds: exercise.exerciseFactIds?.filter((eFid) => factId !== eFid),
                });
              }}
            >
              <RemoveIcon />
            </IconButton>
          </Stack>
        ))}
      </Stack>
    </>
  );
};
